/**
 * PlanGraphView — the build-project plan rendered as a fixed, 2-D enterprise grid.
 *   - x-axis = phase column = node.workspace_type (Context · Research · Build ·
 *     Document · Package); columns are hairline dividers.
 *   - y-axis = steps: cluster lanes (≥2 clusters) else a dependency-tier fallback
 *     ("Step 1/2/3…"). Lanes are OPAQUE zebra bands painted UNDER edges; the
 *     zebra continues into the unused area below the content.
 *   - flow = top→bottom; edges drop bottom→top and render ABOVE the bands
 *     (zIndex 5) / below the cards (zIndex 10); incident edges light up on hover.
 *   - card accent = PHASE colour; corner dot = STATUS.
 *
 * The grid is anchored TOP-LEFT in a bounded movable workspace: scroll down/right
 * within bounds, never up/left past the origin, and it never re-centers (even on
 * AI updates). Single-click a node → bottom detail drawer; double-click → open
 * the workspace. Pure view-layer — DESIGN.md tokens only.
 */

import React, { useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  useViewport,
  useReactFlow,
  MarkerType,
  ReactFlowInstance,
  BaseEdge,
  EdgeProps,
  getSmoothStepPath,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, Minus, Maximize2, ChevronDown } from 'lucide-react';
import type { Cluster } from '../../types/cluster';
import BuildWelcomePanel from './BuildWelcomePanel';
import PlanNodeDetailPanel from './PlanNodeDetailPanel';

// =============================================================================
// TYPES
// =============================================================================

export interface PlanNodeData {
  node_id: string;
  text: string;
  node_type: 'task' | 'component' | 'dependency' | 'milestone';
  status: 'planned' | 'active' | 'done' | 'blocked' | 'revised' | 'needs_review';
  workspace_id?: string | null;
  workspace_type?: string | null;
  cluster_id?: string | null;
  validation_status?: string | null;
  workspace_validation_status?: string | null;
  workspace_started?: boolean;
  completion_governance_status?: string | null;
  completion_governance_reason?: string | null;
  completion_governance_scope?: string | null;
  deliverable?: string | null;
  deliverable_role?: string | null;
  deliverable_shape_id?: string | null;
  deliverable_shape?: string | null;
  project?: Record<string, any> | null;
  validation_stats?: Record<string, any> | null;
  validator_stats?: Record<string, any> | null;
  validation_verdict?: string | null;
  overall_verdict?: string | null;
  deliverable_verdict?: string | null;
  metadata?: Record<string, any> | null;
  /** Full 2-6 sentence planner description (LogicNode.content) — detail panel. */
  description?: string | null;
  /** set_plan_node_output marked this node delivered (status derives 'done'). */
  is_terminal?: boolean;
}

export interface PlanEdgeData {
  edge_id: string;
  from_node_id: string;
  to_node_id: string;
  edge_type: 'feeds_into' | 'depends_on' | 'validates';
  output_spec?: string;
  has_output?: boolean;
  label?: string | null;
}

interface PlanGraphViewProps {
  nodes: PlanNodeData[];
  edges: PlanEdgeData[];
  clusters?: Record<string, Cluster>;
  objective?: string;
  onNodeClick?: (nodeId: string, workspaceId?: string | null) => void;
}

// =============================================================================
// PHASE COLUMNS (x-axis) — column identity is workspace_type
// =============================================================================

const PHASE_COLUMNS: { key: string; label: string }[] = [
  { key: 'institution_context', label: 'Context' },
  { key: 'research', label: 'Research' },
  { key: 'production', label: 'Build' },
  { key: 'documentation', label: 'Document' },
  { key: 'output_package', label: 'Package' },
];

const COLUMN_INDEX: Record<string, number> = {
  institution_context: 0,
  research: 1,
  production: 2,
  documentation: 3,
  output_package: 4,
};

function columnOf(workspaceType?: string | null): number {
  if (workspaceType && COLUMN_INDEX[workspaceType] !== undefined) return COLUMN_INDEX[workspaceType];
  return 0;
}

// Grid geometry (world coordinates)
const COLUMN_WIDTH = 300;
const CARD_WIDTH = 232;
const CARD_HEIGHT = 56; // h-14
const COLUMN_PAD = (COLUMN_WIDTH - CARD_WIDTH) / 2; // 34
const ROW_STEP = 76;           // 56px card + 20px gap between stacked cards
const LANE_TOP_PAD = 12;
const LANE_PAD_BOTTOM = 12;
const GRID_WIDTH = COLUMN_WIDTH * PHASE_COLUMNS.length;
const UNGROUPED_LANE = '__ungrouped__';

// Frozen-axis chrome (screen coordinates)
const HEADER_H = 32;
const GUTTER_W = 140;

// Zoom bounds. The zebra canvas is sized to fill the viewport even at MIN_ZOOM,
// so zooming out never reveals un-striped background.
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.25;

// Phase → accent colour (the left card bar; meaningful + gives visual rhythm).
// Exported so the node detail panel's phase chip uses the SAME colour.
export const PHASE_ACCENT: Record<string, string> = {
  institution_context: 'var(--fg-tertiary)',
  research: 'var(--link)',
  production: 'var(--secondary)',
  documentation: 'var(--warning)',
  output_package: 'var(--primary)',
};

// Status → dot colour (greys until work starts, then blue/green).
// Exported so the node detail panel's status pill + input dots stay in sync.
export const STATUS_COLOR: Record<string, string> = {
  planned: 'var(--fg-tertiary)',
  active: 'var(--link)',
  done: 'var(--success)',
  blocked: 'var(--danger)',
  revised: 'var(--warning)',
  needs_review: 'var(--warning)',
};

// =============================================================================
// CUSTOM NODE COMPONENTS
// =============================================================================

const HIDDEN_HANDLE: React.CSSProperties = { opacity: 0, width: 1, height: 1, border: 'none', minWidth: 0, minHeight: 0 };

// Hover/selection highlight is driven by context (NOT by rebuilding the node
// array), so node identity/geometry never changes on hover → no re-measure loop
// → no "shake". Only colour/opacity change.
interface PlanHoverState { activeId: string | null; neighborIds: Set<string> }
const PlanHoverContext = React.createContext<PlanHoverState>({ activeId: null, neighborIds: new Set() });

function PlanGraphNode({ data }: { data: PlanNodeData & { onClick?: () => void } }) {
  const { activeId, neighborIds } = React.useContext(PlanHoverContext);
  const accent = PHASE_ACCENT[data.workspace_type || ''] || 'var(--fg-tertiary)';
  const statusColor = STATUS_COLOR[data.status] || 'var(--fg-tertiary)';

  const isActive = activeId === data.node_id;          // hovered/selected → full fill
  const isNeighbor = !isActive && neighborIds.has(data.node_id); // connected → bordered
  const dimmed = !!activeId && !isActive && !isNeighbor;

  const borderColor = isActive ? accent : isNeighbor ? 'var(--primary)' : 'var(--border-strong)';
  const borderWidth = isActive || isNeighbor ? 2 : 1; // fat border for active/connected (box-border → no size shift)

  return (
    <div
      onClick={data.onClick}
      className="relative w-[232px] h-14 pl-3 pr-4 py-2 rounded-md shadow-bark-md cursor-pointer transition-colors flex items-center"
      style={{
        // Active node fills with its workspace-type (phase) colour.
        background: isActive ? `color-mix(in srgb, ${accent} 22%, var(--surface))` : 'var(--surface)',
        border: `${borderWidth}px solid ${borderColor}`,
        borderLeft: `3px solid ${accent}`,
        opacity: dimmed ? 0.4 : 1,
      }}
      title={data.status}
    >
      {/* Two target + two source handles so each edge can pick the side that
          routes cleanly (exit right toward a later phase, else drop down). */}
      <Handle id="t-top" type="target" position={Position.Top} isConnectable={false} style={HIDDEN_HANDLE} />
      <Handle id="t-left" type="target" position={Position.Left} isConnectable={false} style={HIDDEN_HANDLE} />
      <Handle id="s-bottom" type="source" position={Position.Bottom} isConnectable={false} style={HIDDEN_HANDLE} />
      <Handle id="s-right" type="source" position={Position.Right} isConnectable={false} style={HIDDEN_HANDLE} />
      <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: statusColor }} />
      <p className="text-[14px] font-medium text-fg leading-snug line-clamp-2">{data.text}</p>
    </div>
  );
}

/** Phase-column band — full-height vertical hairline divider only. */
function ColumnBandNode({ data }: { data: { width: number; height: number } }) {
  return (
    <div
      aria-hidden
      style={{
        width: data.width, height: data.height, boxSizing: 'border-box',
        borderLeft: '1px solid var(--border)', background: 'transparent', pointerEvents: 'none',
      }}
    />
  );
}

/** Step lane band — OPAQUE zebra row (continues into the unused area below). */
function LaneBandNode({ data }: { data: { width: number; height: number; shaded: boolean } }) {
  return (
    <div
      aria-hidden
      style={{
        width: data.width, height: data.height, boxSizing: 'border-box',
        borderTop: '1px solid var(--border)',
        background: data.shaded ? 'var(--bg-warm)' : 'var(--surface)',
        pointerEvents: 'none',
      }}
    />
  );
}

const nodeTypes = {
  planNode: PlanGraphNode,
  columnBand: ColumnBandNode,
  laneBand: LaneBandNode,
};

// Orthogonal edge whose vertical segment is forced into the gutter just to the
// RIGHT of the source column (data.centerX), instead of ReactFlow's default
// geometric midpoint. For edges spanning >1 column the default midpoint routes
// the long horizontal run along the SOURCE's row — i.e. straight behind the
// sibling cards stacked in the next column, so the edge reads as if those cards
// are connected. Breaking early drops into the empty column gutter first, then
// travels horizontally along the TARGET's row (empty in the intermediate
// columns, since downstream nodes sit alone in lower rows). For adjacent columns
// data.centerX equals the midpoint, so those edges are unchanged.
function GutterStepEdge({
  id, sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  markerEnd, style, data,
}: EdgeProps) {
  const [path] = getSmoothStepPath({
    sourceX, sourceY, sourcePosition,
    targetX, targetY, targetPosition,
    borderRadius: 12,
    centerX: typeof data?.centerX === 'number' ? data.centerX : undefined,
  });
  return <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd} />;
}

const edgeTypes = { gutterStep: GutterStepEdge };

const DECORATION_TYPES = new Set(['columnBand', 'laneBand']);

interface EdgeLabel { id: string; text: string; x: number; y: number; anchor: string }

/**
 * Edge labels as a viewport-synced HTML overlay (same reliable pattern as the
 * frozen header/gutter). Each lit edge's label is positioned in the column gap
 * (after the source) or the row gap (same column) so it never hides behind a
 * node. Rendered above the cards so the chip stays readable.
 */
function EdgeLabels({ labels }: { labels: EdgeLabel[] }) {
  const vp = useViewport();
  if (!labels.length) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 12, overflow: 'hidden' }}>
      {labels.map((l) => (
        <div
          key={l.id}
          style={{
            position: 'absolute',
            left: l.x * vp.zoom + vp.x,
            top: l.y * vp.zoom + vp.y,
            transform: l.anchor,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '1px 6px',
            fontSize: 10,
            fontWeight: 500,
            color: 'var(--fg-secondary)',
            whiteSpace: 'nowrap',
          }}
        >
          {l.text}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// FROZEN AXES (screen-space HTML overlays, viewport-synced)
// =============================================================================

function PhaseHeader() {
  const vp = useViewport();
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: HEADER_H,
        overflow: 'hidden', pointerEvents: 'none', zIndex: 5,
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}
    >
      {PHASE_COLUMNS.map((col, i) => (
        <div
          key={col.key}
          style={{
            position: 'absolute', top: 0, height: HEADER_H,
            left: i * COLUMN_WIDTH * vp.zoom + vp.x, width: COLUMN_WIDTH * vp.zoom,
            borderLeft: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}
        >
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-secondary)', whiteSpace: 'nowrap' }}>
            {col.label}
          </span>
        </div>
      ))}
    </div>
  );
}

interface RowBand { key: string; name: string; top: number; height: number; shaded: boolean }

function StepGutter({ bands }: { bands: RowBand[] }) {
  const vp = useViewport();
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: GUTTER_W,
        overflow: 'hidden', pointerEvents: 'none', zIndex: 5,
        background: 'var(--surface)', borderRight: '1px solid var(--border)',
      }}
    >
      {/* Each row label cell carries the SAME zebra shading as its grid row, and
          the filler rows continue the pattern so the gutter never looks flat. */}
      {bands.map((band) => (
        <div
          key={band.key}
          style={{
            position: 'absolute', left: 0, width: GUTTER_W,
            top: band.top * vp.zoom + vp.y, height: band.height * vp.zoom,
            borderTop: '1px solid var(--border)',
            background: band.shaded ? 'var(--bg-warm)' : 'var(--surface)',
            padding: '8px 12px', boxSizing: 'border-box', overflow: 'hidden',
          }}
        >
          {band.name && (
            <span style={{ display: 'block', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fg-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {band.name}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Custom zoom controls that zoom ANCHORED at the grid's top-left corner, so the
 *  board stays locked to the top-left as you zoom (the default centre-anchored
 *  zoom drifts it toward the middle). Reset re-pins top-left at the fit zoom. */
function ZoomControls({ onReset }: { onReset: () => void }) {
  const rf = useReactFlow();
  const zoomAtTopLeft = (factor: number) => {
    const { x, y, zoom } = rf.getViewport();
    const wx = (GUTTER_W - x) / zoom; // world point currently under the top-left corner
    const wy = (HEADER_H - y) / zoom;
    const z = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * factor));
    rf.setViewport({ x: GUTTER_W - wx * z, y: HEADER_H - wy * z, zoom: z }, { duration: 150 });
  };
  const btn = 'w-7 h-7 flex items-center justify-center text-fg-secondary hover:bg-bg-warm hover:text-fg transition-colors';
  return (
    <div
      className="absolute left-3 bottom-3 z-10 flex flex-col rounded-md bg-surface border border-border-default shadow-bark-sm overflow-hidden divide-y divide-border-default"
      style={{ pointerEvents: 'auto' }}
    >
      <button className={btn} onClick={() => zoomAtTopLeft(1.2)} aria-label="Zoom in"><Plus className="w-4 h-4" /></button>
      <button className={btn} onClick={() => zoomAtTopLeft(1 / 1.2)} aria-label="Zoom out"><Minus className="w-4 h-4" /></button>
      <button className={btn} onClick={onReset} aria-label="Reset view"><Maximize2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function CornerBox() {
  return (
    <div
      style={{
        position: 'absolute', top: 0, left: 0, width: GUTTER_W, height: HEADER_H,
        background: 'var(--surface)', borderRight: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)', zIndex: 6, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', paddingLeft: 12,
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-secondary)' }}>
        Plan
      </span>
    </div>
  );
}

// =============================================================================
// OVERVIEW HEADER (above the grid) — objective + workspace status counts
// =============================================================================

function OverviewStat({ value, label, dot }: { value: number; label: string; dot?: string }) {
  return (
    <div className="flex flex-col items-end min-w-0">
      <span className="text-[18px] font-semibold text-fg leading-none flex items-center gap-1.5">
        {dot && <span className="w-2 h-2 rounded-full" style={{ background: dot }} />}
        {value}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-fg-tertiary mt-1 whitespace-nowrap max-w-full overflow-hidden text-ellipsis">{label}</span>
    </div>
  );
}

/**
 * Objective block — collapsed to 2 lines by default; when the clamped text
 * actually overflows (measured, re-checked on resize) a subtle show-more
 * affordance appears. Expanding grows the header (the grid below yields).
 * Full text always available via the title attribute.
 */
function ObjectiveBlock({ objective }: { objective?: string }) {
  const text = objective || 'Untitled project';
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  // Only measure while clamped — scrollHeight > clientHeight means hidden text.
  React.useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      if (!expanded) setOverflowing(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, expanded]);

  // While expanded, keep the affordance visible so the user can collapse again.
  const expandable = overflowing || expanded;
  const toggle = () => setExpanded((v) => !v);

  return (
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-fg-tertiary mb-0.5">Objective</p>
      <p
        ref={textRef}
        title={text}
        onClick={expandable ? toggle : undefined}
        className={`text-[15px] text-fg leading-snug ${expanded ? '' : 'line-clamp-2'}${expandable ? ' cursor-pointer' : ''}`}
      >
        {text}
      </p>
      {expandable && (
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-fg-secondary hover:text-fg transition-colors"
        >
          {expanded ? 'Show less' : 'Show more'}
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}
    </div>
  );
}

// =============================================================================
// GRID LAYOUT — column = workspace_type, lane = cluster OR dependency tier
// =============================================================================

interface LaneBand {
  key: string;
  name: string;
  top: number;
  height: number;
  index: number;
}

interface GridLayout {
  positions: Record<string, { x: number; y: number }>;
  lanes: LaneBand[];
  gridWidth: number;
  gridHeight: number;
}

function computeDepths(nodes: PlanNodeData[], edges: PlanEdgeData[]): Map<string, number> {
  const inDegree = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  nodes.forEach((n) => { inDegree.set(n.node_id, 0); outgoing.set(n.node_id, []); });
  edges.forEach((e) => {
    if (!outgoing.has(e.from_node_id) || !inDegree.has(e.to_node_id)) return;
    outgoing.get(e.from_node_id)!.push(e.to_node_id);
    inDegree.set(e.to_node_id, (inDegree.get(e.to_node_id) || 0) + 1);
  });
  const depth = new Map<string, number>();
  const queue: string[] = [];
  nodes.forEach((n) => { if ((inDegree.get(n.node_id) || 0) === 0) { queue.push(n.node_id); depth.set(n.node_id, 0); } });
  while (queue.length > 0) {
    const id = queue.shift()!;
    const d = depth.get(id) || 0;
    for (const target of outgoing.get(id) || []) {
      if ((depth.get(target) ?? -1) < d + 1) depth.set(target, d + 1);
      const newIn = (inDegree.get(target) || 1) - 1;
      inDegree.set(target, newIn);
      if (newIn === 0) queue.push(target);
    }
  }
  nodes.forEach((n) => { if (!depth.has(n.node_id)) depth.set(n.node_id, 0); });
  return depth;
}

function layoutPlanGrid(
  nodes: PlanNodeData[],
  edges: PlanEdgeData[],
  clusters?: Record<string, Cluster>,
): GridLayout {
  const positions: Record<string, { x: number; y: number }> = {};
  const depth = computeDepths(nodes, edges);
  const orderIndex = new Map<string, number>();
  nodes.forEach((n, i) => orderIndex.set(n.node_id, i));

  const realClusters = new Set(nodes.map((n) => n.cluster_id).filter((c): c is string => !!c));
  const useClusters = realClusters.size >= 2;
  const distinctTiers = new Set(nodes.map((n) => depth.get(n.node_id) ?? 0));
  const singleTier = distinctTiers.size <= 1;

  const laneKeyOf = (n: PlanNodeData) =>
    useClusters ? (n.cluster_id || UNGROUPED_LANE) : `tier_${depth.get(n.node_id) ?? 0}`;

  const laneLabelOf = (key: string) => {
    if (useClusters) return key === UNGROUPED_LANE ? 'Ungrouped' : (clusters?.[key]?.name || 'Section');
    if (singleTier) return 'All work';
    return `Step ${Number(key.slice('tier_'.length)) + 1}`;
  };

  const laneMembers = new Map<string, PlanNodeData[]>();
  nodes.forEach((n) => {
    const k = laneKeyOf(n);
    if (!laneMembers.has(k)) laneMembers.set(k, []);
    laneMembers.get(k)!.push(n);
  });

  const laneMinDepth = (k: string) => Math.min(...laneMembers.get(k)!.map((n) => depth.get(n.node_id) ?? 0));

  const laneKeys = Array.from(laneMembers.keys()).sort((a, b) => {
    if (!useClusters) return Number(a.slice('tier_'.length)) - Number(b.slice('tier_'.length));
    if (a === UNGROUPED_LANE) return 1;
    if (b === UNGROUPED_LANE) return -1;
    const da = laneMinDepth(a);
    const db = laneMinDepth(b);
    if (da !== db) return da - db;
    return laneLabelOf(a).localeCompare(laneLabelOf(b));
  });

  const lanes: LaneBand[] = [];
  let y = 0;
  laneKeys.forEach((k, laneIdx) => {
    const members = laneMembers.get(k)!;
    const byColumn = new Map<number, PlanNodeData[]>();
    members.forEach((n) => {
      const c = columnOf(n.workspace_type);
      if (!byColumn.has(c)) byColumn.set(c, []);
      byColumn.get(c)!.push(n);
    });
    let maxStack = 1;
    byColumn.forEach((cellNodes) => {
      cellNodes.sort((a, b) => {
        const da = depth.get(a.node_id) ?? 0;
        const db = depth.get(b.node_id) ?? 0;
        if (da !== db) return da - db;
        return (orderIndex.get(a.node_id) ?? 0) - (orderIndex.get(b.node_id) ?? 0);
      });
      maxStack = Math.max(maxStack, cellNodes.length);
    });
    const laneHeight = LANE_TOP_PAD + maxStack * ROW_STEP + LANE_PAD_BOTTOM;
    byColumn.forEach((cellNodes, c) => {
      cellNodes.forEach((n, i) => {
        positions[n.node_id] = { x: c * COLUMN_WIDTH + COLUMN_PAD, y: y + LANE_TOP_PAD + i * ROW_STEP };
      });
    });
    lanes.push({ key: k, name: laneLabelOf(k), top: y, height: laneHeight, index: laneIdx });
    y += laneHeight;
  });

  return { positions, lanes, gridWidth: GRID_WIDTH, gridHeight: y };
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function PlanGraphView({
  nodes: planNodes,
  edges: planEdges,
  clusters,
  objective,
  onNodeClick,
}: PlanGraphViewProps) {
  const layout = useMemo(() => layoutPlanGrid(planNodes, planEdges, clusters), [planNodes, planEdges, clusters]);

  const summary = useMemo(() => {
    let done = 0, prog = 0, blocked = 0, notStarted = 0;
    planNodes.forEach((n) => {
      if (n.status === 'done') done++;
      else if (n.status === 'active' || n.status === 'revised' || n.status === 'needs_review') prog++;
      else if (n.status === 'blocked') blocked++;
      else notStarted++;
    });
    return { total: planNodes.length, done, prog, blocked, notStarted };
  }, [planNodes]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const selectedNode = useMemo(() => planNodes.find((n) => n.node_id === selectedId) || null, [planNodes, selectedId]);
  // Full-node lookup for the detail panel (it shows the status dot + title of
  // each connected node, not just the title).
  const nodeById = useMemo(() => {
    const m = new Map<string, PlanNodeData>();
    planNodes.forEach((n) => m.set(n.node_id, n));
    return (id: string) => m.get(id);
  }, [planNodes]);

  // Active node (hover wins over selection) + its neighbours — drives the
  // context-based node highlight and the edge light-up.
  const activeId = hoveredId || selectedId;
  const neighborIds = useMemo(() => {
    const s = new Set<string>();
    if (!activeId) return s;
    planEdges.forEach((e) => {
      if (e.from_node_id === activeId) s.add(e.to_node_id);
      if (e.to_node_id === activeId) s.add(e.from_node_id);
    });
    return s;
  }, [activeId, planEdges]);
  const hoverCtx = useMemo<PlanHoverState>(() => ({ activeId, neighborIds }), [activeId, neighborIds]);

  // Labels for the active node's incident edges, positioned in the column gap
  // (column jump) or the row gap (same column). Rendered by the EdgeLabels
  // overlay (viewport-synced) so they're reliable + never hidden behind a node.
  const litLabels = useMemo<EdgeLabel[]>(() => {
    if (!activeId) return [];
    const colOf = (id: string) => columnOf(planNodes.find((n) => n.node_id === id)?.workspace_type);
    const out: EdgeLabel[] = [];
    planEdges.forEach((e) => {
      if (e.from_node_id !== activeId && e.to_node_id !== activeId) return;
      const raw = planNodes.find((n) => n.node_id === e.from_node_id)?.deliverable || e.label || '';
      if (!raw) return;
      const text = raw.length > 26 ? `${raw.slice(0, 26)}…` : raw;
      // Anchor the label at the FAR (non-active) node, on the edge where it
      // attaches there. Every incident edge shares the active node as one
      // endpoint (they fan in/out at that hub), so labelling the far end spreads
      // the chips across the distinct connected nodes — no fighting at the hub —
      // while keeping each chip ON its edge.
      const activeIsSource = e.from_node_id === activeId;
      const otherId = activeIsSource ? e.to_node_id : e.from_node_id;
      const op = layout.positions[otherId];
      if (!op) return;
      const oIsSource = !activeIsSource; // is the FAR node the edge's source?
      const horizontal = colOf(e.to_node_id) > colOf(e.from_node_id);
      let x: number, y: number, anchor: string;
      if (horizontal) {
        // edge meets the far node on a horizontal run at its vertical centre
        y = op.y + CARD_HEIGHT / 2;
        if (oIsSource) { x = op.x + CARD_WIDTH + 8; anchor = 'translate(0, -50%)'; }  // leaves far node's RIGHT
        else { x = op.x - 8; anchor = 'translate(-100%, -50%)'; }                      // enters far node's LEFT
      } else {
        // edge meets the far node on a vertical run at its horizontal centre
        x = op.x + CARD_WIDTH / 2;
        if (oIsSource) { y = op.y + CARD_HEIGHT + 8; anchor = 'translate(-50%, 0)'; }  // leaves far node's BOTTOM
        else { y = op.y - 8; anchor = 'translate(-50%, -100%)'; }                      // enters far node's TOP
      }
      out.push({ id: e.edge_id, text, x, y, anchor });
    });
    // Safety net: if two edges still anchor at the same far-node row (same x
    // band, near-equal y), push the later one down so the chips never overlap.
    const MIN_GAP = 18;
    const bands = new Map<number, EdgeLabel[]>();
    out.forEach((l) => {
      const band = Math.round(l.x / 40);
      if (!bands.has(band)) bands.set(band, []);
      bands.get(band)!.push(l);
    });
    bands.forEach((group) => {
      if (group.length < 2) return;
      group.sort((a, b) => a.y - b.y);
      for (let i = 1; i < group.length; i++) {
        if (group[i].y - group[i - 1].y < MIN_GAP) group[i].y = group[i - 1].y + MIN_GAP;
      }
    });
    return out;
  }, [activeId, planEdges, planNodes, layout]);

  // Pin/sizing state — set once on first layout, then never re-pinned (the board
  // stays put on AI updates). viewMinH extends the zebra canvas to ≥ the viewport
  // so the grid stays top-aligned (never re-centers) and the bands continue.
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const rfWrapRef = useRef<HTMLDivElement>(null);
  const [isReactFlowReady, setIsReactFlowReady] = useState(false);
  const [hasPinned, setHasPinned] = useState(false);
  const [pinZoom, setPinZoom] = useState(0.7);
  const [viewMinH, setViewMinH] = useState(0);
  const [viewMinW, setViewMinW] = useState(0);

  // The zebra canvas extends past the content to cover the whole movable
  // workspace at any zoom (sized for MIN_ZOOM at pin time) — so zooming out
  // never reveals un-striped background, in any direction.
  const canvasH = Math.max(layout.gridHeight, viewMinH);
  const canvasW = Math.max(layout.gridWidth, viewMinW);

  // All horizontal bands: the real Step lanes (named) + filler rows that
  // continue the zebra into the empty area below. Shared by the grid (laneBand
  // nodes) and the row-label gutter so shading + labels stay in lockstep.
  const rowBands = useMemo<RowBand[]>(() => {
    const arr: RowBand[] = layout.lanes.map((l) => ({
      key: l.key, name: l.name, top: l.top, height: l.height, shaded: l.index % 2 === 0,
    }));
    let fy = layout.gridHeight;
    let fi = layout.lanes.length;
    while (fy < canvasH - 1) {
      arr.push({ key: `filler_${fi}`, name: '', top: fy, height: ROW_STEP, shaded: fi % 2 === 0 });
      fy += ROW_STEP;
      fi += 1;
    }
    return arr;
  }, [layout, canvasH]);

  const { rfNodes, rfEdges } = useMemo(() => {
    const columnNodes: Node[] = PHASE_COLUMNS.map((_, i) => ({
      id: `col_${i}`,
      type: 'columnBand',
      position: { x: i * COLUMN_WIDTH, y: 0 },
      data: { width: COLUMN_WIDTH, height: canvasH },
      draggable: false, selectable: false, connectable: false, zIndex: 0,
      style: { width: COLUMN_WIDTH, height: canvasH, pointerEvents: 'none' as const },
    }));

    // One zebra band per row (lanes + fillers), spanning the full canvas width
    // so the rows extend edge-to-edge even when zoomed out.
    const laneNodes: Node[] = rowBands.map((band) => ({
      id: `lane_${band.key}`,
      type: 'laneBand',
      position: { x: 0, y: band.top },
      data: { width: canvasW, height: band.height, shaded: band.shaded },
      draggable: false, selectable: false, connectable: false, zIndex: 0,
      style: { width: canvasW, height: band.height, pointerEvents: 'none' as const },
    }));

    const cardNodes: Node[] = planNodes.map((pn) => ({
      id: pn.node_id,
      type: 'planNode',
      position: layout.positions[pn.node_id] || { x: 0, y: 0 },
      data: { ...pn, onClick: () => setSelectedId(pn.node_id) },
      zIndex: 10,
    }));

    const rfNodes: Node[] = [...columnNodes, ...laneNodes, ...cardNodes];

    const deliverableById = new Map<string, string | null | undefined>();
    const colById = new Map<string, number>();
    planNodes.forEach((n) => {
      deliverableById.set(n.node_id, n.deliverable);
      colById.set(n.node_id, columnOf(n.workspace_type));
    });

    // Edges are calm at rest (faint, no labels, no arrowheads) and only light up
    // — with their label + direction arrow — for the hovered/selected node (see
    // the edges sync effect). Keeps a dense fan-out (e.g. model → many docs) from
    // becoming spaghetti while preserving the dependency info on focus.
    const rfEdges: Edge[] = planEdges.map((pe) => {
      const raw = deliverableById.get(pe.from_node_id) || pe.label || undefined;
      const fullLabel = raw && raw.length > 26 ? `${raw.slice(0, 26)}…` : raw;
      // Route to avoid crossing the node stack: if the target is in a later
      // phase column, exit the source's RIGHT into the target's LEFT (runs in
      // the column gap); otherwise drop bottom→top.
      const fromCol = colById.get(pe.from_node_id) ?? 0;
      const horizontal = (colById.get(pe.to_node_id) ?? 0) > fromCol;
      // For horizontal edges, force the vertical bend into the gutter just right
      // of the source column (see GutterStepEdge) so the long run hugs the
      // target's empty row instead of passing behind the next column's cards.
      const centerX = horizontal ? (fromCol + 1) * COLUMN_WIDTH : undefined;
      return {
        id: pe.edge_id,
        source: pe.from_node_id,
        target: pe.to_node_id,
        sourceHandle: horizontal ? 's-right' : 's-bottom',
        targetHandle: horizontal ? 't-left' : 't-top',
        type: horizontal ? 'gutterStep' : 'smoothstep',
        pathOptions: { borderRadius: 12 },
        zIndex: 5, // above the lane bands (0), always below the cards (10)
        data: { fullLabel, centerX },
        // Calm + opaque at rest (grey); only lights up (teal) for the active node.
        style: { stroke: 'var(--border-strong)', strokeWidth: 1.5, opacity: 1, strokeDasharray: pe.edge_type === 'depends_on' ? '5,5' : undefined },
      };
    });

    return { rfNodes, rfEdges };
  }, [planNodes, planEdges, layout, canvasH, canvasW, rowBands]);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Sync nodes (layout/selection) — never on hover, so hovering can't churn the
  // node layer (the source of the old "shake").
  React.useEffect(() => { setNodes(rfNodes); }, [rfNodes, setNodes]);

  // Sync edges. Every edge is calm grey + opaque at all times. When a node is
  // hovered/selected, ONLY its incident edges light up (teal + thicker + label
  // + arrow); the rest are left exactly as they are.
  React.useEffect(() => {
    const active = hoveredId || selectedId;
    setEdges(rfEdges.map((e) => {
      if (!active) return e;
      const incident = e.source === active || e.target === active;
      if (!incident) return e; // unchanged — opaque grey
      return {
        ...e,
        zIndex: 6, // lit, but still below the cards (10)
        style: { ...e.style, stroke: 'var(--secondary)', strokeWidth: 2.5, opacity: 1 },
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: 'var(--secondary)' },
      };
    }));
  }, [rfEdges, hoveredId, selectedId, setEdges]);

  // Pin the grid TOP-LEFT and fit to width — ONCE. Capture the zoom + a minimum
  // canvas height (≥ viewport) so the grid never re-centers and the zebra fills.
  React.useEffect(() => {
    if (!isReactFlowReady || rfNodes.length === 0 || hasPinned) return;
    const t = setTimeout(() => {
      const inst = reactFlowInstance.current;
      const wrap = rfWrapRef.current;
      if (!inst || !wrap) return;
      const availW = Math.max(240, wrap.clientWidth - GUTTER_W - 24);
      const zoom = Math.max(MIN_ZOOM, Math.min(1.1, availW / layout.gridWidth));
      inst.setViewport({ x: GUTTER_W, y: HEADER_H, zoom }, { duration: 0 });
      setPinZoom(zoom);
      // Size the zebra canvas for the most-zoomed-out case (MIN_ZOOM), so the
      // stripes fill the viewport at every zoom level — never blank on zoom-out.
      setViewMinW(Math.max(layout.gridWidth, (wrap.clientWidth - GUTTER_W) / MIN_ZOOM));
      setViewMinH(Math.max(layout.gridHeight, (wrap.clientHeight - HEADER_H) / MIN_ZOOM + LANE_PAD_BOTTOM));
      setHasPinned(true);
    }, 0);
    return () => clearTimeout(t);
  }, [isReactFlowReady, rfNodes.length, hasPinned, layout.gridWidth, layout.gridHeight]);

  const isInitialFitPending = rfNodes.length > 0 && !hasPinned;

  if (planNodes.length === 0) {
    return <BuildWelcomePanel />;
  }

  // Bounded movable workspace anchored top-left. The negative insets (sized for
  // the most-zoomed-out case) let the grid origin sit just past the frozen
  // gutter+header at any zoom; lockTopLeft (below) stops it drifting the other way.
  const translateExtent: [[number, number], [number, number]] = [
    [-GUTTER_W / MIN_ZOOM, -HEADER_H / MIN_ZOOM],
    [layout.gridWidth + COLUMN_PAD, canvasH],
  ];

  // Hard-lock the top-left corner: never let the origin drift right of the gutter
  // or below the header, so the board stays pinned to the corner on pan AND zoom.
  const lockTopLeft = (vp: { x: number; y: number; zoom: number }) => {
    if (vp.x > GUTTER_W || vp.y > HEADER_H) {
      reactFlowInstance.current?.setViewport({ x: Math.min(vp.x, GUTTER_W), y: Math.min(vp.y, HEADER_H), zoom: vp.zoom });
    }
  };

  const resetView = () =>
    reactFlowInstance.current?.setViewport({ x: GUTTER_W, y: HEADER_H, zoom: pinZoom }, { duration: 200 });

  return (
    <PlanHoverContext.Provider value={hoverCtx}>
    <div className="w-full h-full flex flex-col bg-bg-warm">
      {/* Project overview header — objective owns the width; the stat chips stay
          one right-aligned row and compress (label ellipsis) before the
          objective gives up space. */}
      <header className="flex-shrink-0 bg-surface border-b border-border-default px-5 py-3 flex items-start justify-between gap-6">
        <ObjectiveBlock objective={objective} />
        <div className="flex items-start justify-end gap-5 min-w-0">
          <OverviewStat value={summary.total} label="Workspaces" />
          <OverviewStat value={summary.done} label="Done" dot="var(--success)" />
          <OverviewStat value={summary.prog} label="In progress" dot="var(--link)" />
          <OverviewStat value={summary.notStarted} label="Not started" dot="var(--fg-tertiary)" />
          {summary.blocked > 0 && <OverviewStat value={summary.blocked} label="Blocked" dot="var(--danger)" />}
        </div>
      </header>

      <div ref={rfWrapRef} className="flex-1 min-h-0 relative">
        {isInitialFitPending && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg-warm text-sm text-fg-tertiary">
            Loading graph...
          </div>
        )}

        <ReactFlow
          className={isInitialFitPending ? 'opacity-0' : undefined}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onPaneClick={() => setSelectedId(null)}
          onNodeMouseEnter={(_event, node) => { if (node.type && !DECORATION_TYPES.has(node.type)) setHoveredId(node.id); }}
          onNodeMouseLeave={() => setHoveredId(null)}
          onNodeDoubleClick={(_event, node) => {
            if (node.type && !DECORATION_TYPES.has(node.type)) {
              onNodeClick?.(node.id, (node.data as PlanNodeData).workspace_id);
            }
          }}
          onInit={(instance) => { reactFlowInstance.current = instance; setIsReactFlowReady(true); }}
          onMove={(_event, vp) => lockTopLeft(vp)}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          translateExtent={translateExtent}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={true}
          panOnScroll
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
        >
          <PhaseHeader />
          <StepGutter bands={rowBands} />
          <CornerBox />
          <EdgeLabels labels={litLabels} />
          <ZoomControls onReset={resetView} />
        </ReactFlow>
      </div>

      {selectedNode && (
        <PlanNodeDetailPanel
          /* Keyed by node so switching nodes REMOUNTS the drawer — its local
             fetched state (e.g. the BAR-505 regulatory scope) must not flash
             the previous node's data on the new node's first paint. Drawer
             height survives (module-level session memory). */
          key={selectedNode.node_id}
          node={selectedNode}
          edges={planEdges}
          nodeById={nodeById}
          onOpenWorkspace={(nid, wid) => onNodeClick?.(nid, wid)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
    </PlanHoverContext.Provider>
  );
}
